import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Layout } from "@/components/layout";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { CartProvider } from "@/lib/cart-context";
import { CountryProvider } from "@/lib/country-context";
import { CustomerRoute, RestaurantRoute } from "@/components/protected-route";

import ChatPage from "@/pages/chat";
import RestaurantsPage from "@/pages/restaurants";
import RestaurantDetailPage from "@/pages/restaurant-detail";
import OrdersPage from "@/pages/orders";
import OrderDetailPage from "@/pages/order-detail";
import SigninPage from "@/pages/signin";
import SignupPage from "@/pages/signup";

import RestaurantLandingPage from "@/pages/restaurant-portal/landing";
import RestaurantSigninPage from "@/pages/restaurant-portal/signin";
import RestaurantSignupPage from "@/pages/restaurant-portal/signup";
import RestaurantDashboardPage from "@/pages/restaurant-portal/dashboard";

import { AdminAuthProvider } from "@/lib/admin-auth-context";
import { AdminRoute } from "@/components/admin-route";
import AdminSigninPage from "@/pages/admin/signin";
import AdminDashboardPage from "@/pages/admin/dashboard";
import AdminOrdersPage from "@/pages/admin/orders";
import AdminRestaurantsPage from "@/pages/admin/restaurants";
import AdminRidersPage from "@/pages/admin/riders";
import AdminCustomersPage from "@/pages/admin/customers";
import AdminActivityPage from "@/pages/admin/activity";

function NotFound() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6 text-center">
      <div>
        <p className="font-serif text-4xl font-bold">404</p>
        <p className="text-muted-foreground mt-2">We couldn&apos;t find that page.</p>
      </div>
    </div>
  );
}

function RestaurantPortalIndex() {
  const { isRestaurant, loading } = useAuth();
  if (loading) return null;
  return isRestaurant ? <RestaurantDashboardPage /> : <RestaurantLandingPage />;
}

function CustomerShell() {
  return (
    <CustomerRoute>
      <Layout>
        <Routes>
          <Route path="/" element={<ChatPage />} />
          <Route path="/restaurants" element={<RestaurantsPage />} />
          <Route path="/restaurants/:id" element={<RestaurantDetailPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:id" element={<OrderDetailPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </CustomerRoute>
  );
}

function App() {
  return (
    <AuthProvider>
      <AdminAuthProvider>
      <CountryProvider>
        <CartProvider>
          <TooltipProvider>
            <BrowserRouter>
              <Routes>
                {/* Standalone routes (no main layout) */}
                <Route path="/signin" element={<SigninPage />} />
                <Route path="/signup" element={<SignupPage />} />

                {/* Restaurant portal */}
                <Route path="/restaurant-portal" element={<RestaurantPortalIndex />} />
                <Route path="/restaurant-portal/signin" element={<RestaurantSigninPage />} />
                <Route path="/restaurant-portal/signup" element={<RestaurantSignupPage />} />
                <Route
                  path="/restaurant-portal/dashboard"
                  element={<RestaurantRoute><RestaurantDashboardPage /></RestaurantRoute>}
                />

                {/* Admin console */}
                <Route path="/admin/signin" element={<AdminSigninPage />} />
                <Route element={<AdminRoute />}>
                  <Route path="/admin" element={<AdminDashboardPage />} />
                  <Route path="/admin/orders" element={<AdminOrdersPage />} />
                  <Route path="/admin/restaurants" element={<AdminRestaurantsPage />} />
                  <Route path="/admin/riders" element={<AdminRidersPage />} />
                  <Route path="/admin/customers" element={<AdminCustomersPage />} />
                  <Route path="/admin/activity" element={<AdminActivityPage />} />
                </Route>

                {/* Everything else uses the customer shell */}
                <Route path="/*" element={<CustomerShell />} />
              </Routes>
            </BrowserRouter>
            <Toaster />
          </TooltipProvider>
        </CartProvider>
      </CountryProvider>
      </AdminAuthProvider>
    </AuthProvider>
  );
}

export default App;
